import { useState, useRef, useCallback, useEffect } from 'react';
import { Dialog } from '@mui/material';
import { IconPencil, IconX } from '@tabler/icons-react';
import * as Sentry from '@sentry/react';

import { Button, Input } from 'ui';

import { uploadFile } from '../../core/utils';
import FileDrop from '../file-drop/FileDrop';
import { createPresignedUploadUrl, DOCUMENT_CATEGORY_TYPES, DOCUMENT_TYPES } from './utils';
import FileInfo from '../file-info/FileInfo';
import { FILE_STATUS } from '../../core/constants';
import { deleteDocument, editDocument, getDocuments as getDocumentsApi } from '../../api';
import IconButton from '../buttons/IconButton';

const allowedFileTypes = {
  'image/jpeg': ['.jpeg', '.jpg'],
  'image/png': ['.png'],
  'application/pdf': ['.pdf'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
};

const UploadSupportingDocumentInput = () => {
  const [pendingDocuments, setPendingDocuments] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [fileStatus, setFileStatus] = useState(FILE_STATUS.pending);
  const [progress, setProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const uploadControllerRef = useRef();

  // Dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editDescription, setEditDescription] = useState('');
  const [editDocName, setEditDocName] = useState('');

  const getDocuments = useCallback(async () => {
    const response = await getDocumentsApi();
    setDocuments(response.data);
  }, []);

  useEffect(() => {
    getDocuments();
  }, [getDocuments]);

  const handleUploadProgress = (progressEvent) => {
    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
    setProgress(percentCompleted);
  };

  const handleUploadFiles = async (files) => {
    setErrorMessage('');
    setFileStatus(FILE_STATUS.pending);
    try {
      await Promise.all(
        files.map(async (file) => {
          const { url: uploadUrl } = await createPresignedUploadUrl(
            file.name,
            DOCUMENT_CATEGORY_TYPES.supporting,
            DOCUMENT_TYPES.document,
          );

          const arrayBuffer = await file.arrayBuffer();
          uploadControllerRef.current = new AbortController();
          await uploadFile(uploadUrl, arrayBuffer, file.type, {
            onUploadProgress: handleUploadProgress,
            signal: uploadControllerRef.current.signal,
          });
        }),
      );

      await getDocuments();
      setPendingDocuments([]);
      setFileStatus(FILE_STATUS.saved);
    } catch (err) {
      Sentry.captureException(err);
      setFileStatus(FILE_STATUS.failed);
      setErrorMessage('Uploading failed');
    }
  };

  const handleDeleteFile = async (name) => {
    setErrorMessage('');
    setLoading(true);
    try {
      await deleteDocument(name);
      setDocuments((prevDocuments) => prevDocuments.filter((doc) => doc.name !== name));
    } catch (err) {
      Sentry.captureException(err);
      setFileStatus(FILE_STATUS.failed);
      setErrorMessage('Delete failed');
    }
    setLoading(false);
  };

  const handleCancelUpload = async () => {
    setLoading(true);
    try {
      uploadControllerRef.current?.abort();
      uploadControllerRef.current = undefined;
      setPendingDocuments([]);
      uploadControllerRef.current?.abort();
      uploadControllerRef.current = undefined;
      setProgress(0);
      await getDocuments();
    } catch (err) {
      Sentry.captureException(err);
      setFileStatus(FILE_STATUS.failed);
      setErrorMessage('Cancel upload failed');
    }
    setLoading(false);
  };

  const handleFileChange = (files) => {
    setErrorMessage('');
    setPendingDocuments(files);
    handleUploadFiles(files);
  };

  // Handler for opening the edit dialog
  const handleOpenEditDialog = (doc) => {
    setEditDocName(doc.name);
    setEditDescription(doc.description || '');
    setEditDialogOpen(true);
  };

  // Handler for confirming the edit
  const handleConfirmEdit = async () => {
    setLoading(true);
    try {
      await editDocument(editDocName, { description: editDescription });
      setDocuments((prevDocs) =>
        prevDocs.map((doc) => (doc.name === editDocName ? { ...doc, description: editDescription } : doc)),
      );
    } catch (error) {
      Sentry.captureException(error);
      setErrorMessage('Edit failed');
    }

    setEditDialogOpen(false);
    setLoading(false);
  };

  // Handler for canceling the edit
  const handleCancelEdit = () => {
    setEditDialogOpen(false);
  };

  return (
    <div className="space-y-4">
      <FileDrop
        onChange={handleFileChange}
        allowedFileTypes={allowedFileTypes}
        containerClassName="!w-full !h-[122px]"
        shortDescription="PDF, DOCX, JPG, or PNG files, max size 50MB."
        maxFileSize={50 * 10 ** 6} // 50MB -> in bytes
        multiple={true}
        disabled={pendingDocuments.length > 0 && fileStatus === FILE_STATUS.pending}
      />

      {pendingDocuments.map((document) => (
        <FileInfo
          key={document.name}
          name={document.name}
          onDelete={() => handleDeleteFile(document.name)}
          onCancel={handleCancelUpload}
          status={fileStatus}
          progress={progress}
          loading={loading}
          errorMessage={errorMessage}
        />
      ))}

      {documents.map((document) => (
        <FileInfo
          key={document.name}
          name={document.name}
          onDelete={() => handleDeleteFile(document.name)}
          status={FILE_STATUS.saved}
        >
          <div className="flex items-center space-x-1">
            <p className={`text-sm text-gray-400 ${!document.description && 'italic'}`}>
              {document.description || 'No description provided'}
            </p>
            <IconButton
              Icon={<IconPencil size={16} className="text-gray-400" />}
              color="transparent"
              className="!p-1"
              srOnly="Edit description"
              onClick={() => handleOpenEditDialog(document)}
            />
          </div>
        </FileInfo>
      ))}

      <Dialog
        onClose={handleCancelEdit}
        open={editDialogOpen}
        sx={{ '.MuiPaper-root': { padding: 0, margin: 0, borderRadius: '12px', width: '100%' } }}
      >
        <div className="space-y-6 p-6">
          <div className="relative flex">
            <h3 className="text-h6 font-semibold">Edit Document Description</h3>

            <IconButton
              Icon={<IconX size={20} className="text-gray-400" />}
              srOnly="Close"
              onClick={handleCancelEdit}
              color="transparent"
              className="absolute -right-1 -top-1 !p-1"
            />
          </div>

          <p className="text-base">
            Enter a description for {editDocName} to give us additional context about the document.
          </p>

          <Input
            label="Description"
            size="xl"
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            containerClassName="flex-1"
            required
          />

          <div className="flex justify-end space-x-3">
            <Button title="Cancel" onClick={handleCancelEdit} disabled={loading} />

            <Button
              onClick={handleConfirmEdit}
              title={loading ? 'Loading...' : 'Confirm'}
              color="primary"
              disabled={loading}
              loading={loading}
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default UploadSupportingDocumentInput;
